import { PermissionCode } from '@gb-ticket/shared-types';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminTableFrameClassName } from '@/platform/features/admin-panel';
import { MasterDataListState } from '@/platform/features/master-data/master-data-list-state';
import {
  useCreateCurrency,
  useCurrencies,
  usePatchCurrency,
} from '@/platform/features/master-data/use-master-data';

export function CurrenciesPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.MASTER_DATA_MANAGE);
  const currencies = useCurrencies();

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Währungen"
        description="Der Code und die Nachkommastellen bleiben Ganzzahlen. Euro ist der Anfangsdatensatz."
      />
      <MasterDataListState
        pending={currencies.isPending}
        pendingLabel="Währungen werden geladen …"
        error={currencies.isError ? currencies.error : null}
        errorLabel="Die Währungen konnten nicht geladen werden."
        empty={currencies.data?.items.length === 0}
        emptyLabel="Noch keine Währung."
      >
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead className="text-right">Nachkommastellen</TableHead>
                <TableHead>Rundung</TableHead>
                <TableHead>Status</TableHead>
                {canWrite ? <TableHead>Aktion</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {currencies.data?.items.map((item) => {
                const open = item.roundingVersions.find((version) => version.validTo == null);
                const closed = item.roundingVersions.filter((version) => version.validTo != null);
                return (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-sm">{item.code}</TableCell>
                    <TableCell className="text-right tabular-nums">{item.minorUnits}</TableCell>
                    <TableCell>
                      {open?.roundingMode ?? '—'}
                      {closed.length > 0 ? (
                        <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                          {closed.map((version) => (
                            <li key={version.validFrom}>
                              historisch · {version.roundingMode} · bis {version.validTo}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {item.isActive ? (
                        <Badge variant="outline">Aktiv</Badge>
                      ) : (
                        <Badge variant="secondary">inaktiv</Badge>
                      )}
                    </TableCell>
                    {canWrite ? (
                      <TableCell>
                        {item.isActive ? <InactivateCurrency id={item.id} /> : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </MasterDataListState>
      {canWrite ? <CurrencyForm /> : null}
    </section>
  );
}

function InactivateCurrency({ id }: { id: string }) {
  const patch = usePatchCurrency(id);
  const [pending, setPending] = useState(false);

  async function inactivate() {
    setPending(true);
    try {
      await patch.mutateAsync({ isActive: false });
      toast.success('Währung inaktiviert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Währung konnte nicht inaktiviert werden.',
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <Button type="button" variant="outline" disabled={pending} onClick={() => void inactivate()}>
      Inaktivieren
    </Button>
  );
}

function CurrencyForm() {
  const create = useCreateCurrency();
  const [code, setCode] = useState('');
  const [minorUnits, setMinorUnits] = useState('2');
  const [validFrom, setValidFrom] = useState('');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({
        code,
        minorUnits: Number(minorUnits),
        validFrom,
        roundingMode: 'half_up',
      });
      toast.success('Währung angelegt.');
      setCode('');
      setMinorUnits('2');
      setValidFrom('');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Währung konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <form className="max-w-xl space-y-4" onSubmit={(event) => void onSubmit(event)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="currency-code">Code</Label>
        <Input id="currency-code" value={code} onChange={(event) => setCode(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="currency-units">Nachkommastellen</Label>
        <Input
          id="currency-units"
          type="number"
          min={0}
          max={4}
          value={minorUnits}
          onChange={(event) => setMinorUnits(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="currency-from">Gültig ab</Label>
        <Input
          id="currency-from"
          placeholder="JJJJ-MM-TT"
          value={validFrom}
          onChange={(event) => setValidFrom(event.target.value)}
        />
      </div>
      <Button type="submit" disabled={create.isPending}>
        Währung anlegen
      </Button>
    </form>
  );
}
